import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import produce from 'immer';
import { differenceBy, intersectionBy, intersectionWith, uniqBy } from 'lodash';
import isEqual from 'lodash/isEqual';

import api from '../../../api';
import { currenciesSelector } from '../../../redux/selectors/SessionData';
import {
  getActivePrices,
  getCopyPrices,
  getIsChanged,
  getIsEqualPrices,
  hasPricesErrors,
} from '../SubSections/PricesTable/util';

const usePrice = ({ product, update, setProductData }) => {
  const history = useHistory();
  const { id } = useParams();
  const currencies = useSelector(currenciesSelector);

  const { parentId: historyParentId } = history?.location?.state || {};

  const { parentId = historyParentId } = product || {};
  const [isLoading, setIsLoading] = useState(false);
  const [isPriceTouched, setIsPriceTouched] = useState(false);
  const [prices, setPrices] = useState([]);
  const [parentPrices, setParentPrices] = useState();
  const [currentPrices, setCurrentPrices] = useState();
  const [scheduledPrices, setScheduledPrices] = useState([]);

  const defCurrencyOptions = useMemo(() => {
    const sourceDefCurrencyOptions =
      (currentPrices?.length && currentPrices) || (parentPrices?.length && parentPrices);

    if (!sourceDefCurrencyOptions) {
      return currencies;
    }

    return intersectionWith(
      currencies || [],
      sourceDefCurrencyOptions,
      ({ id: currencyId }, { currency }) => currencyId === currency,
    );
  }, [currencies, currentPrices, parentPrices]);

  const isPricesChanged = useMemo(
    () => !isLoading && getIsChanged(prices, currentPrices),
    [isLoading, prices, currentPrices],
  );

  const isPriceHasError = useMemo(
    () => hasPricesErrors(currentPrices, product),
    [currentPrices, product],
  );

  const savePriceData = async (productId = product.id, customerId = product.customerId) => {
    if (!isPricesChanged) return;

    setIsLoading(true);
    const removedPrices = differenceBy(prices, currentPrices, 'id');
    const updatedPrices = currentPrices.filter(
      (currentPrice) =>
        currentPrice.id &&
        !getIsEqualPrices(
          currentPrice,
          prices.find(({ id }) => id === currentPrice.id),
        ),
    );
    const newPrices = currentPrices.filter((currentPrice) => !currentPrice.id);

    newPrices.push(...getCopyPrices(updatedPrices));

    const tasks = [];

    removedPrices.forEach((removedPrice) => {
      tasks.push(api.deletePriceById(removedPrice.id));
    });

    newPrices.forEach((newPrice) => {
      const startDate = Math.max(newPrice.startDate, new Date().getTime());
      const endDate = newPrice.endDate && Math.max(newPrice.endDate, startDate);

      tasks.push(
        api.addNewPrice({
          ...newPrice,
          startDate,
          endDate,
          productId,
          customerId,
        }),
      );
    });

    await Promise.allSettled(tasks);

    setIsLoading(false);
  };

  const setPriceFromSource = (source) => {
    switch (source) {
      case 'prev':
        return prices?.length && prices !== currentPrices ? () => setCurrentPrices(prices) : null;
      case 'parent':
        return parentPrices?.length &&
          !isEqual(getCopyPrices(parentPrices), getCopyPrices(currentPrices))
          ? () => setCurrentPrices(getCopyPrices(parentPrices))
          : null;
      default:
        return null;
    }
  };

  useEffect(() => {
    setScheduledPrices([]);
  }, [update]);

  useEffect(() => {
    if (id && id !== 'add') {
      setIsLoading(true);
      api
        .getPricesByProductId(id)
        .then((pricesData) => {
          const productPrices = pricesData?.data.items || [];
          const activeProductPrices = getActivePrices(productPrices);
          setPrices(activeProductPrices);
          setCurrentPrices(activeProductPrices);

          setScheduledPrices((draft) =>
            uniqBy(
              [...draft, ...productPrices].sort((a, b) => b.startDate - a.startDate),
              'id',
            ),
          );
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [id, update]);

  useEffect(() => {
    if (id && id !== 'add') {
      api
        .getPricesHistoryByProductId(id)
        .then((pricesHistoryData) => {
          setScheduledPrices((draft) =>
            uniqBy(
              [...draft, ...(pricesHistoryData?.data.items || [])].sort(
                (a, b) => b.startDate - a.startDate,
              ),
              'id',
            ),
          );
        })
        .catch(() => {});
    }
  }, [id, update]);

  useEffect(() => {
    if (!parentId && id === 'add') {
      setParentPrices([]);
      setCurrentPrices([]);
    }
  }, [id, parentId]);

  useEffect(() => {
    parentId &&
      api.getPricesByProductId(parentId).then((pricesData) => {
        const parentProductPrices = getActivePrices(pricesData?.data.items || []);
        setParentPrices(parentProductPrices);
        id === 'add' && setCurrentPrices(getCopyPrices(parentProductPrices));
      });
  }, [id, parentId]);

  useEffect(() => {
    if (
      isPriceTouched &&
      !defCurrencyOptions.some(
        ({ id: currencyOptionId }) => currencyOptionId === product?.prices.defaultCurrency,
      )
    ) {
      setProductData(
        produce((draft) => {
          draft.prices.defaultCurrency = defCurrencyOptions[0]?.id;
        }),
      );
    }
  }, [defCurrencyOptions, isPriceTouched, product?.prices.defaultCurrency, setProductData]);

  useEffect(() => {
    isPricesChanged && setIsPriceTouched(true);
  }, [isPricesChanged]);

  return {
    currentPrices,
    scheduledPrices,
    prices,
    isPricesChanged,
    setCurrentPrices,
    savePriceData,
    isPriceHasError,
    setPriceFromSource,
    parentPrices,
    defCurrencyOptions,
  };
};

export default usePrice;
